import { getBootcampStep } from "@Components/game/session-results/bootcamp/bootcamp-completion.config";
import BootcampMetricResult from "@Components/game/session-results/bootcamp/bootcamp-metric-result";
import BootcampStepProgress from "@Components/game/session-results/bootcamp/bootcamp-step-progress";
import Icon from "@Components/generic/icon";
import { BootcampChallengeStep } from "@Constants/bootcamp";
import { useSessionResultsScenarioData, useSessionResultsStaticContext } from "@Context/game/activity/session-results-context";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";

interface BootcampCompletionContentProps {
  challengeStep: BootcampChallengeStep;
  metricScore?: number;
  metricTarget?: number;
  completedSteps?: number;
  failed?: boolean;
}

export const BootcampCompletionContent = ({
  challengeStep,
  metricScore,
  metricTarget,
  completedSteps,
  failed = false,
}: BootcampCompletionContentProps) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const step = getBootcampStep(challengeStep)!;
  const isFinal = challengeStep === BootcampChallengeStep.FINAL;
  const completedStepCount = completedSteps ?? (failed ? Math.max(step.progress - 1, 0) : step.progress);
  const copyPrefix = `session-results.bootcamp.${step.translationKey}`;
  const titleKey = failed
    ? "session-results.bootcamp.failed.title"
    : isFinal
      ? "session-results.bootcamp-complete.title"
      : "session-results.bootcamp.title";
  const descriptionKey = failed ? "session-results.bootcamp.failed.description" : `${copyPrefix}.description`;

  return (
    <Stack gap={6} position="relative">
      <Stack textAlign="center" alignItems="center" gap={2}>
        <Typography variant="text3xl700" color="dsBase.v200">
          {i18n(titleKey)}
        </Typography>
        <Typography variant="text1xl400" color="dsNeutral.v300" lineHeight={1.5}>
          {i18n(descriptionKey)}
        </Typography>
      </Stack>

      {step.metric && <BootcampMetricResult metric={step.metric} score={metricScore} target={metricTarget} />}

      {!isFinal && <BootcampStepProgress completedSteps={completedStepCount} />}

      <Box
        sx={{
          backgroundColor: "dsSurface.v300",
          border: `1px solid ${theme.palette.dsSurface.v400}`,
          borderRadius: 6,
          padding: theme.spacing(3),
        }}
      >
        <Typography variant="text1xl400" color="dsNeutral.v300" lineHeight={1.5} width="100%">
          {i18n(`${copyPrefix}.tip-body`)}
        </Typography>
      </Box>
    </Stack>
  );
};

const BootcampCompletion = ({ challengeStep }: { challengeStep: BootcampChallengeStep }) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { scenarioData } = useSessionResultsScenarioData();
  const { nextChallenge, handleNextChallengeClick } = useSessionResultsStaticContext();
  const step = getBootcampStep(challengeStep)!;
  const metricResult = scenarioData?.performanceStats.find(result => result.type === step.metric);

  return (
    <Stack minHeight="100vh" alignItems="center" justifyContent="center" padding={theme.spacing(14, 3, 6)} position="relative" zIndex={1}>
      <Stack gap={4} width="100%" maxWidth={930}>
        <BootcampCompletionContent challengeStep={challengeStep} metricScore={metricResult?.score} metricTarget={metricResult?.target} />
        <Button onClick={handleNextChallengeClick} disabled={!nextChallenge} startIcon={<Icon icon={faPlay} />} data-shape="pill">
          {i18n("session-results.bootcamp.continue")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default BootcampCompletion;
